import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ThemeContext } from '../utils/themes/ThemeContext';

const OrgCard = ({ org, index, theme, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        cursor: "pointer",
        borderRadius: { xs: "20px", md: "24px" },
        overflow: "hidden",
        aspectRatio: "3/4",
        width: "100%",
        animation: `fadeUp 0.55s ease forwards`,
        animationDelay: `${index * 0.1}s`,

        outline: hovered
          ? `2px solid ${theme.PrimaryColor}`
          : "2px solid transparent",
        outlineOffset: "2px",
        transition: "outline-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease",
        transform: hovered ? "translateY(-6px) scale(1.01)" : "translateY(0) scale(1)",
        boxShadow: hovered
          ? `0 20px 48px rgba(0,0,0,0.35), 0 0 0 1px ${theme.PrimaryColor}44`
          : "0 6px 24px rgba(0,0,0,0.2)",
      }}
    >
      <Box
        component="img"
        src={`${org.picture_url}`}
        alt={org.name}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.5s ease",
          transform: hovered ? "scale(1.07)" : "scale(1)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: hovered
            ? `linear-gradient(to top, ${theme.AccentColor}ee 0%, ${theme.AccentColor}88 40%, transparent 100%)`
            : `linear-gradient(to top, ${theme.AccentColor}cc 0%, ${theme.AccentColor}44 50%, transparent 100%)`,
          transition: "background 0.35s ease",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: { xs: "20px 16px 16px", md: "24px 20px 20px" },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontWeight: 700,
            fontSize: { xs: 16, sm: 18, md: 20 },
            color: theme.NeutralColor,
            lineHeight: 1.25,
            letterSpacing: "-0.2px",
          }}
        >
          {org.name}
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            mt: "10px",
            px: "12px",
            py: "5px",
            borderRadius: "100px",
            backgroundColor: theme.PrimaryColor,
            opacity: hovered ? 1 : 0,
            transform: hovered ? "translateY(0)" : "translateY(6px)",
            transition: "opacity 0.25s ease, transform 0.25s ease",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 11,
              color: theme.NeutralColor,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            Vote now
          </Typography>
          <span style={{ color: theme.NeutralColor, fontSize: 12 }}>→</span>
        </Box>
      </Box>
    </Box>
  );
};

const OrganizationsPage = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations`
      )
      .then((res) => setOrgs(res.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (org) => {
    navigate(`/vote/${org.id}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        paddingTop: "120px",
        backgroundColor: theme.NeutralColor,
        color: theme.AccentColor,
        display: "flex",
        flexDirection: "column",
        px: { xs: "16px", sm: "32px", md: "64px" },
        pb: { xs: "48px", sm: "64px" },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          mb: { xs: "36px", md: "52px" },
          animation: "fadeUp 0.5s ease forwards",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontWeight: 800,
            fontSize: { xs: 28, sm: 36, md: 48 },
            lineHeight: 1.1,
            letterSpacing: "-1px",
            mb: "10px",
            textAlign: "center",
          }}
        >
          Choose your{" "}
          <Box
            component="span"
            sx={{
              // Animated shimmer on the accent word
              background: `linear-gradient(90deg, ${theme.PrimaryColor}, ${theme.SecondaryColor ?? theme.PrimaryColor}, ${theme.PrimaryColor})`,
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "shimmer 3s linear infinite",
            }}
          >
            organization
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: 14, sm: 16 },
            color: theme.GreyColor ?? `${theme.AccentColor}88`,
            maxWidth: "400px",
            lineHeight: 1.6,
            textAlign: "center",
            mx: "auto",
          }}
        >
          Pick a section below to start voting on its members.
        </Typography>
      </Box>

      {/* States */}
      {loading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontFamily: "Poppins", color: theme.GreyColor, fontSize: 15 }}>
            Loading organizations…
          </Typography>
        </Box>
      )}

      {error && !loading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontFamily: "Poppins", color: theme.PrimaryColor, fontSize: 15 }}>
            Could not load organizations. Please try again later.
          </Typography>
        </Box>
      )}

      {!loading && !error && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: { xs: "12px", sm: "16px", md: "20px", lg: "24px" },
          }}
        >
          {orgs.map((org, i) => (
            <OrgCard
                key={org.id}
                org={org}
                index={i}
                theme={theme}
                onClick={() => handleSelect(org)}
            />
          ))}
        </Box>
      )}

      {!loading && !error && orgs.length === 0 && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontFamily: "Poppins", color: theme.GreyColor, fontSize: 15 }}>
            No organizations found.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OrganizationsPage;
